import { Fragment } from "react";

import { IAuditLog } from "../../../../@types/AuditLog";
import { ModalJsonContainer } from "../AdminPage.styles";
import {
  ChangeSummaryCaption,
  ChangeSummaryContainer,
  ChangeTable,
  ChangeTableCell,
  ChangeTableHeader,
  EmptyChangeNotice,
  NullValue,
  RawJsonDisclosure,
} from "./AuditLog.styles";

interface IAuditLogChangeSummaryProps {
  auditLog: IAuditLog;
}

type ChangedFieldValue = { old?: unknown; new?: unknown };

type ChangedFieldsMap = Record<string, ChangedFieldValue>;

type JsonObject = Record<string, unknown>;

function safeParse(raw: string | undefined): unknown {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

function humanize(fieldName: string): string {
  return fieldName
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

function formatValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined) return <NullValue>—</NullValue>;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toString();
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function isPlainObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isChangedFieldsMap(value: unknown): value is ChangedFieldsMap {
  if (!isPlainObject(value)) return false;
  if ("_error" in value) return false;
  return Object.values(value).every(
    (entry) => isPlainObject(entry) && ("old" in entry || "new" in entry)
  );
}

function sortedEntries<T>(obj: Record<string, T>): Array<[string, T]> {
  return Object.entries(obj).sort(([a], [b]) => a.localeCompare(b));
}

function RawJson({ label, raw }: { label: string; raw: string | undefined }) {
  if (!raw) return null;
  let formatted = raw;
  try {
    formatted = JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    // leave as-is
  }
  return (
    <RawJsonDisclosure>
      <summary>{label}</summary>
      <ModalJsonContainer>{formatted}</ModalJsonContainer>
    </RawJsonDisclosure>
  );
}

/**
 * Renders the drill-down for one audit row — the portal replacement for
 * Retool's raw `JSONExplorer`, which just dumped `changedFields` / `newData`
 * / `oldData` depending on the operation. Same source fields, but shown as a
 * before/after table with the raw JSON kept one click away.
 */
export function AuditLogChangeSummary({
  auditLog,
}: IAuditLogChangeSummaryProps) {
  const operation = auditLog.operation?.toUpperCase();

  if (operation === "UPDATE") {
    const parsed = safeParse(auditLog.changedFields);
    if (isChangedFieldsMap(parsed)) {
      const entries = sortedEntries(parsed);
      if (entries.length === 0) {
        return (
          <ChangeSummaryContainer>
            <EmptyChangeNotice>
              Update recorded, but no field-level changes were captured.
            </EmptyChangeNotice>
            <RawJson label="View raw JSON" raw={auditLog.newData} />
          </ChangeSummaryContainer>
        );
      }
      return (
        <ChangeSummaryContainer>
          <ChangeSummaryCaption>
            {entries.length} field{entries.length === 1 ? "" : "s"} changed.
          </ChangeSummaryCaption>
          <ChangeTable layout="diff">
            <ChangeTableHeader>Field</ChangeTableHeader>
            <ChangeTableHeader>Before</ChangeTableHeader>
            <ChangeTableHeader>After</ChangeTableHeader>
            {entries.map(([key, change]) => (
              <Fragment key={key}>
                <ChangeTableCell tone="field">{humanize(key)}</ChangeTableCell>
                <ChangeTableCell tone="before">
                  {formatValue(change.old)}
                </ChangeTableCell>
                <ChangeTableCell tone="after">
                  {formatValue(change.new)}
                </ChangeTableCell>
              </Fragment>
            ))}
          </ChangeTable>
          <RawJson label="View raw JSON" raw={auditLog.changedFields} />
        </ChangeSummaryContainer>
      );
    }

    // Fallback: changedFields not usable (null, parse error, or _error marker).
    return (
      <ChangeSummaryContainer>
        <EmptyChangeNotice>
          Couldn&apos;t parse a structured diff for this update. Raw values are
          shown below.
        </EmptyChangeNotice>
        <RawJson label="View old values" raw={auditLog.oldData} />
        <RawJson label="View new values" raw={auditLog.newData} />
      </ChangeSummaryContainer>
    );
  }

  if (operation === "INSERT") {
    const parsed = safeParse(auditLog.newData);
    if (isPlainObject(parsed)) {
      const entries = sortedEntries(parsed);
      return (
        <ChangeSummaryContainer>
          <ChangeSummaryCaption>
            New record created with these values.
          </ChangeSummaryCaption>
          <ChangeTable layout="values">
            <ChangeTableHeader>Field</ChangeTableHeader>
            <ChangeTableHeader>Value</ChangeTableHeader>
            {entries.map(([key, value]) => (
              <Fragment key={key}>
                <ChangeTableCell tone="field">{humanize(key)}</ChangeTableCell>
                <ChangeTableCell tone="after">
                  {formatValue(value)}
                </ChangeTableCell>
              </Fragment>
            ))}
          </ChangeTable>
          <RawJson label="View raw JSON" raw={auditLog.newData} />
        </ChangeSummaryContainer>
      );
    }
    return <RawJson label="View raw JSON" raw={auditLog.newData} />;
  }

  if (operation === "DELETE") {
    const parsed = safeParse(auditLog.oldData);
    if (isPlainObject(parsed)) {
      const entries = sortedEntries(parsed);
      return (
        <ChangeSummaryContainer>
          <ChangeSummaryCaption>
            Record deleted. The following values were removed.
          </ChangeSummaryCaption>
          <ChangeTable layout="values">
            <ChangeTableHeader>Field</ChangeTableHeader>
            <ChangeTableHeader>Value</ChangeTableHeader>
            {entries.map(([key, value]) => (
              <Fragment key={key}>
                <ChangeTableCell tone="field">{humanize(key)}</ChangeTableCell>
                <ChangeTableCell tone="before">
                  {formatValue(value)}
                </ChangeTableCell>
              </Fragment>
            ))}
          </ChangeTable>
          <RawJson label="View raw JSON" raw={auditLog.oldData} />
        </ChangeSummaryContainer>
      );
    }
    return <RawJson label="View raw JSON" raw={auditLog.oldData} />;
  }

  return (
    <ChangeSummaryContainer>
      <RawJson label="View old values" raw={auditLog.oldData} />
      <RawJson label="View new values" raw={auditLog.newData} />
    </ChangeSummaryContainer>
  );
}
